import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  UserIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  CurrencyRupeeIcon,
  MapPinIcon,
  IdentificationIcon,
  BanknotesIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { customerApi } from '../../api/customerApi';

const AddCustomer = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    type: 'regular',
    openingBalance: '',
    balanceType: 'receive', // receive (credit), pay (debit)
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    creditLimit: '',
    gstNumber: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddressChange = (e) => {
    setFormData({ 
      ...formData, 
      address: { ...formData.address, [e.target.name]: e.target.value } 
    });
  };

  const setBalanceType = (type) => {
    setFormData({ ...formData, balanceType: type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Format payload according to backend schema
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        type: formData.type,
        openingBalance: Number(formData.openingBalance) || 0,
        address: formData.address,
        creditLimit: Number(formData.creditLimit) || 0,
        notes: formData.notes
      };

      // Add GST as a document if provided
      if (formData.gstNumber) {
        payload.documents = [{ type: 'gst', number: formData.gstNumber }];
      }

      await customerApi.addCustomer(payload);
      toast.success('Customer added successfully!');
      navigate('/customers');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to add customer';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full pb-10 space-y-6">
      
      {/* Header Section */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/customers')}
          className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors shadow-sm"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Add New Customer</h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-0.5">Create a comprehensive ledger account profile.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* Left Column (Primary Details) */}
        <div className="xl:col-span-2 space-y-6">
          
          {/* Basic Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_15px_-4px_rgba(0,0,0,0.05)] p-6 sm:p-8 transition-colors">
            <h3 className="text-[13px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">Personal Details</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Customer Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Rahul Sharma"
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Phone Number <span className="text-red-500">*</span></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="98765 43210"
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="rahul@example.com"
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing & Address Card */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_15px_-4px_rgba(0,0,0,0.05)] p-6 sm:p-8 transition-colors">
            <h3 className="text-[13px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-100 dark:border-gray-700 pb-4 flex items-center gap-2">
              <MapPinIcon className="h-4 w-4" /> Billing Address
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={formData.address.street}
                  onChange={handleAddressChange}
                  placeholder="123 Market Road, Shop 4"
                  className="block w-full rounded-xl border-0 py-3.5 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                />
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.address.city}
                  onChange={handleAddressChange}
                  placeholder="Mumbai"
                  className="block w-full rounded-xl border-0 py-3.5 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.address.state}
                    onChange={handleAddressChange}
                    placeholder="MH"
                    className="block w-full rounded-xl border-0 py-3.5 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.address.pincode}
                    onChange={handleAddressChange}
                    placeholder="400001"
                    className="block w-full rounded-xl border-0 py-3.5 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[15px] font-medium transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (Financial & Metadata) */}
        <div className="space-y-6">
          
          {/* Opening Balance (Crucial) */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_15px_-4px_rgba(0,0,0,0.05)] p-6 sm:p-8 relative overflow-hidden group transition-colors">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-3xl -mr-10 -mt-10 opacity-60"></div>
            <div className="relative z-10">
              <h3 className="text-[13px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">Opening Balance</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Amount</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <CurrencyRupeeIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="number"
                      name="openingBalance"
                      value={formData.openingBalance}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="block w-full rounded-xl border-0 py-4 pl-12 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[20px] font-extrabold transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Balance Status</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setBalanceType('receive')}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                        formData.balanceType === 'receive' 
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
                          : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:border-green-200 dark:hover:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                    >
                      <span className="text-[13px] font-bold">You will Get</span>
                      <span className="text-[10px] font-medium opacity-80">(Advance)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBalanceType('pay')}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                        formData.balanceType === 'pay' 
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-400' 
                          : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:border-red-200 dark:hover:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                    >
                      <span className="text-[13px] font-bold">You will Give</span>
                      <span className="text-[10px] font-medium opacity-80">(Credit)</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business & Tax Details */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_15px_-4px_rgba(0,0,0,0.05)] p-6 sm:p-8 transition-colors">
            <h3 className="text-[13px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">Business & KYC</h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Customer Type</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <TagIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[14px] font-medium transition-all"
                  >
                    <option value="regular">Regular Customer</option>
                    <option value="wholesale">Wholesale Buyer</option>
                    <option value="vip">VIP Client</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">GST Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <IdentificationIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleChange}
                    placeholder="22AAAAA0000A1Z5"
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[14px] font-medium transition-all uppercase"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2 flex justify-between">
                  <span>Credit Limit</span>
                  <span className="text-gray-400 dark:text-gray-500 font-normal">Optional</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BanknotesIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="creditLimit"
                    value={formData.creditLimit}
                    onChange={handleChange}
                    placeholder="Max credit allowed"
                    className="block w-full rounded-xl border-0 py-3.5 pl-11 pr-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-[#dc343b] text-[14px] font-medium transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
          
        </div>

        {/* Submit Footer Panel */}
        <div className="xl:col-span-3 bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-[0_2px_15px_-4px_rgba(0,0,0,0.05)] p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 transition-colors">
          <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium">Please ensure all required fields (*) are filled before saving.</p>
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 font-bold text-[14px] hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-10 py-3.5 rounded-xl bg-[#dc343b] text-white font-bold text-[14px] hover:bg-[#b91c1c] transition-colors shadow-lg shadow-red-500/30 flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : 'Save Customer'}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default AddCustomer;
